import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ImageUpload } from "@/components/ImageUpload";
import { useAuth } from "@/context/AuthContext";

const field = "w-full h-11 bg-white/[0.04] border border-white/10 px-4 text-sm outline-none focus:border-neon/60 transition-colors";
const label = "text-[11px] font-bold uppercase tracking-[0.2em] text-white/40";

const CreatePetition = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState<any>({
    title: "", target_authority: "", category: "policy", summary: "", body: "",
    goal: 1000, deadline: "", city: "", cover_image: "",
  });
  const [busy, setBusy] = useState(false);

  if (!user)
    return (
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 py-32" data-testid="petition-auth-gate">
        <h1 className="font-bebas text-5xl tracking-wide uppercase">Sign in to draft a petition</h1>
        <button onClick={login} className="mt-8 bg-neon px-8 h-12 text-[12px] font-bold uppercase tracking-[0.2em] hover:pc-glow transition-shadow">Join the circle</button>
      </div>
    );

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...f, goal: Number(f.goal), deadline: f.deadline || null, city: f.city || null, cover_image: f.cover_image || null };
      const { data } = await api.post("/petitions", payload);
      toast.success("Petition submitted for admin review.");
      navigate(`/petitions/${data.petition_id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-5 lg:px-10 py-16" data-testid="create-petition-page">
      <span className="text-[11px] font-bold uppercase tracking-[0.28em] text-neon">Draft</span>
      <h1 className="mt-3 font-bebas text-5xl tracking-wide uppercase">Start a petition</h1>
      <p className="mt-4 text-sm text-white/50">Admin reviews every petition before it goes public. Be specific about your target and ask.</p>

      <form onSubmit={submit} className="mt-10 grid gap-6">
        <div>
          <div className={label}>Title</div>
          <input required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="petition-title-input" className={`mt-2 ${field}`} />
        </div>
        <div className="grid gap-6 sm:grid-cols-2">
          <div>
            <div className={label}>Target authority</div>
            <input required value={f.target_authority} onChange={(e) => setF({ ...f, target_authority: e.target.value })} placeholder="e.g. Ministry of Urban Affairs" data-testid="petition-target-input" className={`mt-2 ${field}`} />
          </div>
          <div>
            <div className={label}>Category</div>
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} data-testid="petition-category-select" className={`mt-2 ${field}`}>
              {["policy", "environment", "education", "health", "civic", "rights"].map((c) => (
                <option key={c} value={c} className="bg-ink-surface">{c}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <div className={label}>One-line summary</div>
          <input required value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} data-testid="petition-summary-input" className={`mt-2 ${field}`} />
        </div>
        <div>
          <div className={label}>Full petition</div>
          <textarea required rows={8} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="petition-body-input" className="mt-2 w-full bg-white/[0.04] border border-white/10 p-4 text-sm outline-none focus:border-neon/60 transition-colors" />
        </div>
        <div className="grid gap-6 sm:grid-cols-3">
          <div>
            <div className={label}>Signature goal</div>
            <input type="number" min={10} value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} data-testid="petition-goal-input" className={`mt-2 ${field}`} />
          </div>
          <div>
            <div className={label}>Deadline</div>
            <input type="date" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} data-testid="petition-deadline-input" className={`mt-2 ${field}`} />
          </div>
          <div>
            <div className={label}>City</div>
            <input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="petition-city-input" className={`mt-2 ${field}`} />
          </div>
        </div>
        <div>
          <ImageUpload value={f.cover_image} onChange={(url) => setF({ ...f, cover_image: url })} label="Cover photo (optional)" testid="petition-cover-upload" />
        </div>
        <button disabled={busy} data-testid="petition-submit-btn" className="mt-2 h-12 bg-neon text-[12px] font-bold uppercase tracking-[0.2em] disabled:opacity-40 hover:pc-glow transition-shadow">
          Submit for review
        </button>
      </form>
    </div>
  );
};

export default CreatePetition;
